import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { fetchEmployeeIncentiveReport } from '../../services/incentiveTicketsService';
import { formatOMR } from '../../utils/formatCurrency';
import { useAuth } from '../../contexts/AuthContext';
import { useRBAC } from '../../contexts/RBACContext';

const COMPANY_NAME = 'Electronic City for IT Services';
const COMPANY_SUBTITLE = 'Project Incentive Management System';

const EmployeeIncentiveReport = () => {
  const { user, userProfile } = useAuth();
  const { canViewAll, canViewOwn, canViewTeam, rbacEmployeeId, MODULE_KEYS, hasPermission } = useRBAC();

  // Determine view scope for this module
  const MODULE = MODULE_KEYS?.EMPLOYEE_INCENTIVE_REPORTS;
  const viewAll  = canViewAll(MODULE);
  const viewOwn  = canViewOwn(MODULE);
  const viewTeam = canViewTeam(MODULE);

  // Derive scope label for UI banner
  const scopeLabel = viewOwn
    ? 'My Incentives'
    : viewTeam
    ? 'My Team Incentives' :'All Employee Incentives';

  const scopeIcon = viewOwn ? 'User' : viewTeam ? 'Users' : 'Globe';
  const scopeColor = viewOwn ? 'text-sky-400' : viewTeam ? 'text-indigo-400' : 'text-emerald-400';
  const scopeBg = viewOwn ? 'bg-sky-500/10 border-sky-500/20' : viewTeam ? 'bg-indigo-500/10 border-indigo-500/20' : 'bg-emerald-500/10 border-emerald-500/20';

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [filters, setFilters] = useState({
    year: '',
    month: '',
    employee: '',
    department: '',
    memberType: '',
    paymentStatus: '',
  });

  const currentYear = new Date()?.getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);
  const months = [
    { value: '1', label: 'January' }, { value: '2', label: 'February' }, { value: '3', label: 'March' },
    { value: '4', label: 'April' }, { value: '5', label: 'May' }, { value: '6', label: 'June' },
    { value: '7', label: 'July' }, { value: '8', label: 'August' }, { value: '9', label: 'September' },
    { value: '10', label: 'October' }, { value: '11', label: 'November' }, { value: '12', label: 'December' },
  ];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      // Build view scope object based on RBAC permissions
      let viewScope = { scope: 'all' };
      if (viewOwn) {
        viewScope = {
          scope: 'own',
          employeeId: rbacEmployeeId || null,
          userEmail: user?.email || null,
          userFullName: userProfile?.full_name || userProfile?.display_name || user?.user_metadata?.full_name || null,
        };
      } else if (viewTeam) {
        // For team scope, we pass the scope flag; team member filtering
        // is done in the service based on teamMemberNames if provided.
        // Without a team roster API, we rely on the service to return all
        // and the manager can use the employee filter to narrow down.
        viewScope = { scope: 'team', teamMemberNames: [] };
      }
      const data = await fetchEmployeeIncentiveReport(filters, viewScope);
      setEmployees(data);
    } catch (err) {
      setError(err?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [filters, viewOwn, viewTeam, rbacEmployeeId, user?.email]);

  useEffect(() => { load(); }, [load]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setSelectedEmployee(null);
  };

  const totalSales = employees?.reduce((s, e) => s + e?.totalSalesIncentive, 0);
  const totalProject = employees?.reduce((s, e) => s + e?.totalProjectIncentive, 0);
  const totalPaid = employees?.reduce((s, e) => s + e?.paidAmount, 0);
  const totalPending = employees?.reduce((s, e) => s + e?.pendingAmount, 0);

  // Export: for view_own users, only export their own row
  const handleExportCSV = () => {
    const today = new Date()?.toISOString()?.split('T')?.[0]?.replace(/-/g, '_');
    const empName = filters?.employee ? `_${filters?.employee?.replace(/\s+/g, '_')}` : '';
    const yearStr = filters?.year ? `_${filters?.year}` : '';
    const rows = [
      ['Employee', 'Department', 'Role', 'Deals Closed', 'Projects Worked', 'Sales Incentive (OMR)', 'Project Incentive (OMR)', 'Total Incentive (OMR)', 'Paid (OMR)', 'Pending (OMR)'],
      ...employees?.map(e => [
        e?.employeeName, e?.department, e?.role,
        e?.dealsClosedCount, e?.projectsWorkedCount,
        (e?.totalSalesIncentive || 0)?.toFixed(3),
        (e?.totalProjectIncentive || 0)?.toFixed(3),
        (e?.totalIncentive || 0)?.toFixed(3),
        (e?.paidAmount || 0)?.toFixed(3),
        (e?.pendingAmount || 0)?.toFixed(3),
      ])
    ]?.map(r => r?.map(c => `"${c}"`)?.join(','))?.join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Employee_Incentive_Report${empName}${yearStr}_${today}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const el = document.getElementById('employee-print-area');
    if (el) el.style.display = 'block';
    window.print();
    setTimeout(() => { if (el) el.style.display = 'none'; }, 1000);
  };

  const generatedDate = new Date()?.toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
  const generatedBy = user?.profile?.full_name || user?.email || 'System';

  // Whether to show the employee count badge (hidden for view_own)
  const showEmployeeCount = !viewOwn;
  // Whether to show company-wide KPI cards (hidden for view_own)
  const showGlobalKPIs = !viewOwn;
  // Whether to show export CSV (hidden for view_own unless they have export permission)
  const canExport = hasPermission(MODULE, 'export_pdf') || hasPermission(MODULE, 'export_excel') || viewAll || viewTeam;

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 12mm; }
          body * { visibility: hidden !important; }
          #employee-print-area, #employee-print-area * { visibility: visible !important; }
          #employee-print-area { position: fixed !important; top: 0 !important; left: 0 !important; width: 100% !important; background: white !important; color: black !important; font-family: Arial, sans-serif !important; font-size: 10pt !important; }
          .no-print { display: none !important; }
          .emp-print-table { width: 100%; border-collapse: collapse; }
          .emp-print-table thead { display: table-header-group; }
          .emp-print-table tfoot { display: table-footer-group; }
          .page-break-avoid { page-break-inside: avoid; }
        }
      `}</style>
      <MainLayout>
        <div className="w-full max-w-7xl mx-auto space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 no-print">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
                {viewOwn ? 'My Incentive Report' : viewTeam ? 'Team Incentive Report' : 'Employee Yearly Incentive Report'}
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                {viewOwn ? 'Your personal incentive summary' : viewTeam ? 'Incentive summary for your team' : 'Yearly incentive summary per employee'}
              </p>
            </div>
            <div className="flex gap-2">
              {canExport && (
                <Button variant="outline" iconName="Download" iconPosition="left" onClick={handleExportCSV}>Export CSV</Button>
              )}
              <Button variant="outline" iconName="Printer" iconPosition="left" onClick={handlePrint}>Print</Button>
            </div>
          </div>

          {/* Scope Banner */}
          <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border ${scopeBg} no-print`}>
            <Icon name={scopeIcon} size={16} className={scopeColor} />
            <span className={`text-sm font-medium ${scopeColor}`}>{scopeLabel}</span>
            {viewOwn && (
              <span className="text-xs text-muted-foreground ml-1">— You can only view your own incentive records</span>
            )}
            {viewTeam && (
              <span className="text-xs text-muted-foreground ml-1">— Showing incentive records for your team members</span>
            )}
            {viewAll && (
              <span className="text-xs text-muted-foreground ml-1">— Full access to all employee incentive records</span>
            )}
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-2xl p-4 shadow-sm no-print">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Year</label>
                <select value={filters?.year} onChange={e => handleFilterChange('year', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">All Years</option>
                  {years?.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Month</label>
                <select value={filters?.month} onChange={e => handleFilterChange('month', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">All Months</option>
                  {months?.map(m => <option key={m?.value} value={m?.value}>{m?.label}</option>)}
                </select>
              </div>
              {/* Employee filter: hidden for view_own (they can only see themselves) */}
              {!viewOwn && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">Employee</label>
                  <input type="text" value={filters?.employee} onChange={e => handleFilterChange('employee', e?.target?.value)} placeholder="Search..."
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
                </div>
              )}
              {!viewOwn && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">Department</label>
                  <input type="text" value={filters?.department} onChange={e => handleFilterChange('department', e?.target?.value)} placeholder="Department..."
                    className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50" />
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Member Type</label>
                <select value={filters?.memberType} onChange={e => handleFilterChange('memberType', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">All Types</option>
                  <option value="SALES">Sales</option>
                  <option value="PROJECT_TEAM">Project Team</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground block mb-1">Payment Status</label>
                <select value={filters?.paymentStatus} onChange={e => handleFilterChange('paymentStatus', e?.target?.value)}
                  className="w-full px-3 py-2 bg-background border border-border rounded-xl text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50">
                  <option value="">All</option>
                  <option value="Pending">Pending</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
            </div>
          </div>

          {/* Summary Cards — shown for all scopes but labeled appropriately */}
          {!loading && employees?.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 no-print">
              {[
                { label: viewOwn ? 'My Sales Incentives' : 'Total Sales Incentives', value: formatOMR(totalSales), icon: 'TrendingUp', color: 'from-blue-500 to-blue-600' },
                { label: viewOwn ? 'My Project Incentives' : 'Total Project Incentives', value: formatOMR(totalProject), icon: 'Users', color: 'from-purple-500 to-purple-600' },
                { label: viewOwn ? 'My Paid Amount' : 'Total Paid', value: formatOMR(totalPaid), icon: 'CheckCircle', color: 'from-success to-emerald-600' },
                { label: viewOwn ? 'My Pending Amount' : 'Total Pending', value: formatOMR(totalPending), icon: 'Clock', color: 'from-warning to-orange-500' },
              ]?.map(card => (
                <div key={card?.label} className="bg-card border border-border rounded-2xl p-4 shadow-sm">
                  <div className={`w-9 h-9 rounded-xl bg-gradient-to-br ${card?.color} flex items-center justify-center mb-3`}>
                    <Icon name={card?.icon} size={18} color="white" />
                  </div>
                  <p className="text-xs text-muted-foreground">{card?.label}</p>
                  <p className="text-lg font-bold text-foreground mt-0.5">{card?.value}</p>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-3 p-4 bg-error/10 border border-error/30 rounded-xl text-error text-sm no-print">
              <Icon name="AlertCircle" size={16} />{error}
            </div>
          )}

          {/* Employee Table */}
          <div className="bg-card border border-border rounded-2xl shadow-sm overflow-hidden no-print">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-semibold text-foreground">
                {viewOwn ? 'My Incentive Summary' : viewTeam ? 'Team Summary' : 'Employee Summary'}
              </h2>
              {/* Hide employee count for view_own users */}
              {showEmployeeCount && (
                <span className="text-sm text-muted-foreground">{employees?.length} employees</span>
              )}
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-7 h-7 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : employees?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Icon name="Users" size={36} className="text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  {viewOwn ? 'No incentive records found for your account' : 'No employee incentive data found'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Employee</th>
                      <th className="text-left px-4 py-3 font-semibold text-muted-foreground">Department</th>
                      <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Deals Closed</th>
                      <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Projects</th>
                      <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Sales Incentive</th>
                      <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Project Incentive</th>
                      <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Total Earned</th>
                      <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Paid</th>
                      <th className="text-right px-4 py-3 font-semibold text-muted-foreground">Pending</th>
                      <th className="text-center px-4 py-3 font-semibold text-muted-foreground">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {employees?.map((emp, idx) => (
                      <React.Fragment key={idx}>
                        <tr
                          className={`hover:bg-muted/10 transition-colors cursor-pointer ${selectedEmployee === idx ? 'bg-primary/5' : ''}`}
                          onClick={() => setSelectedEmployee(selectedEmployee === idx ? null : idx)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary to-accent flex items-center justify-center text-xs font-bold text-white">
                                {emp?.employeeName?.charAt(0)?.toUpperCase()}
                              </div>
                              <span className="font-medium text-foreground">{emp?.employeeName}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-muted-foreground">{emp?.department}</td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-blue-500/15 text-blue-400 text-xs font-bold">{emp?.dealsClosedCount}</span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-purple-500/15 text-purple-400 text-xs font-bold">{emp?.projectsWorkedCount}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-foreground">{formatOMR(emp?.totalSalesIncentive)}</td>
                          <td className="px-4 py-3 text-right text-foreground">{formatOMR(emp?.totalProjectIncentive)}</td>
                          <td className="px-4 py-3 text-right font-bold text-primary">{formatOMR(emp?.totalIncentive)}</td>
                          <td className="px-4 py-3 text-right text-success font-medium">{formatOMR(emp?.paidAmount)}</td>
                          <td className="px-4 py-3 text-right text-warning font-medium">{formatOMR(emp?.pendingAmount)}</td>
                          <td className="px-4 py-3 text-center">
                            <Icon name={selectedEmployee === idx ? 'ChevronUp' : 'ChevronDown'} size={16} className="text-muted-foreground mx-auto" />
                          </td>
                        </tr>
                        {selectedEmployee === idx && emp?.tickets?.length > 0 && (
                          <tr>
                            <td colSpan={10} className="px-4 py-0">
                              <div className="bg-muted/20 rounded-xl border border-border/50 my-2 overflow-hidden">
                                <div className="p-3 border-b border-border/50">
                                  <p className="text-xs font-semibold text-foreground">Ticket Details for {emp?.employeeName}</p>
                                </div>
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b border-border/50 bg-muted/30">
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Ticket #</th>
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Project</th>
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Client</th>
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Type</th>
                                      <th className="text-right px-3 py-2 font-semibold text-muted-foreground">Incentive</th>
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Status</th>
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Payment</th>
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Created</th>
                                      <th className="text-left px-3 py-2 font-semibold text-muted-foreground">Paid Date</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-border/30">
                                    {emp?.tickets?.map((t, ti) => (
                                      <tr key={ti} className="hover:bg-muted/20 transition-colors">
                                        <td className="px-3 py-2 font-mono font-semibold text-primary">{t?.ticketNumber}</td>
                                        <td className="px-3 py-2 text-foreground">{t?.projectName}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{t?.clientName || '—'}</td>
                                        <td className="px-3 py-2">
                                          <span className={`px-1.5 py-0.5 rounded text-xs ${t?.memberType === 'Sales' ? 'bg-blue-500/15 text-blue-400' : 'bg-purple-500/15 text-purple-400'}`}>
                                            {t?.memberType}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-right font-bold text-primary">{formatOMR(t?.incentiveAmount)}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{t?.status}</td>
                                        <td className="px-3 py-2">
                                          <span className={`px-1.5 py-0.5 rounded text-xs ${t?.paymentStatus === 'Paid' ? 'bg-success/15 text-success' : 'bg-muted text-muted-foreground'}`}>
                                            {t?.paymentStatus}
                                          </span>
                                        </td>
                                        <td className="px-3 py-2 text-muted-foreground">{t?.createdDate ? new Date(t?.createdDate)?.toLocaleDateString('en-GB') : '—'}</td>
                                        <td className="px-3 py-2 text-muted-foreground">{t?.paidDate ? new Date(t?.paidDate)?.toLocaleDateString('en-GB') : '—'}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </MainLayout>
      {/* ===== PRINT AREA ===== */}
      <div id="employee-print-area" style={{ display: 'none' }}>
        <div style={{ fontFamily: 'Arial, sans-serif', color: '#111', background: 'white' }}>
          {/* Company Header */}
          <div style={{ borderBottom: '3px solid #1a3a5c', paddingBottom: '12px', marginBottom: '16px' }}>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td style={{ verticalAlign: 'top' }}>
                    <div style={{ fontSize: '16pt', fontWeight: 'bold', color: '#1a3a5c' }}>{COMPANY_NAME}</div>
                    <div style={{ fontSize: '9pt', color: '#6b7280', marginTop: '2px' }}>{COMPANY_SUBTITLE}</div>
                  </td>
                  <td style={{ textAlign: 'right', verticalAlign: 'top' }}>
                    <div style={{ fontSize: '14pt', fontWeight: 'bold', color: '#1a3a5c' }}>
                      {viewOwn ? 'MY INCENTIVE REPORT' : viewTeam ? 'TEAM INCENTIVE REPORT' : 'EMPLOYEE YEARLY INCENTIVE REPORT'}
                    </div>
                    <div style={{ fontSize: '9pt', color: '#6b7280', marginTop: '4px' }}>
                      {filters?.year ? `Year: ${filters?.year}` : 'All Years'}
                      {filters?.employee ? ` · Employee: ${filters?.employee}` : ''}
                      {filters?.department ? ` · Dept: ${filters?.department}` : ''}
                    </div>
                    <div style={{ fontSize: '9pt', color: '#6b7280' }}>Generated: {generatedDate}</div>
                    <div style={{ fontSize: '9pt', color: '#6b7280' }}>Generated By: {generatedBy}</div>
                    <div style={{ fontSize: '9pt', color: '#6b7280' }}>Print Date: {new Date()?.toLocaleDateString('en-GB')}</div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Summary */}
          <div style={{ marginBottom: '16px', padding: '10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <div style={{ fontSize: '10pt', fontWeight: 'bold', color: '#1a3a5c', marginBottom: '8px' }}>SUMMARY</div>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  {[
                    ...(showEmployeeCount ? [{ label: 'Total Employees', value: employees?.length }] : []),
                    { label: viewOwn ? 'My Sales Incentives' : 'Total Sales Incentives', value: formatOMR(totalSales) },
                    { label: viewOwn ? 'My Project Incentives' : 'Total Project Incentives', value: formatOMR(totalProject) },
                    { label: viewOwn ? 'My Paid Amount' : 'Total Paid', value: formatOMR(totalPaid) },
                    { label: viewOwn ? 'My Pending Amount' : 'Total Pending', value: formatOMR(totalPending) },
                  ]?.map((s, i) => (
                    <td key={i} style={{ textAlign: 'center', padding: '6px 8px', border: '1px solid #e2e8f0', background: 'white' }}>
                      <div style={{ fontSize: '8pt', color: '#6b7280' }}>{s?.label}</div>
                      <div style={{ fontSize: '11pt', fontWeight: 'bold', color: '#1a3a5c' }}>{s?.value}</div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Employee Summary Table */}
          <table className="emp-print-table" style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <thead>
              <tr style={{ background: '#1a3a5c' }}>
                {['No.', 'Employee Name', 'Department', 'Role', 'Deals Closed', 'Projects Worked', 'Sales Incentive', 'Project Incentive', 'Total Earned', 'Paid', 'Pending']?.map(h => (
                  <th key={h} style={{ background: '#1a3a5c', color: 'white', padding: '7px 8px', textAlign: 'left', fontSize: '9pt', fontWeight: 'bold', borderRight: '1px solid #2d5a8e' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees?.map((emp, idx) => (
                <tr key={idx} style={{ background: idx % 2 === 0 ? 'white' : '#f8fafc' }}>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{idx + 1}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', fontWeight: 'bold' }}>{emp?.employeeName}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{emp?.department}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb' }}>{emp?.role}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 'bold', color: '#1d4ed8' }}>{emp?.dealsClosedCount}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'center', fontWeight: 'bold', color: '#7c3aed' }}>{emp?.projectsWorkedCount}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>{formatOMR(emp?.totalSalesIncentive)}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right' }}>{formatOMR(emp?.totalProjectIncentive)}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right', fontWeight: 'bold', color: '#1a3a5c' }}>{formatOMR(emp?.totalIncentive)}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right', color: '#059669' }}>{formatOMR(emp?.paidAmount)}</td>
                  <td style={{ padding: '5px 8px', fontSize: '9pt', borderBottom: '1px solid #e5e7eb', textAlign: 'right', color: '#d97706' }}>{formatOMR(emp?.pendingAmount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr style={{ background: '#1a3a5c' }}>
                <td colSpan={6} style={{ padding: '7px 8px', fontSize: '10pt', fontWeight: 'bold', color: 'white' }}>TOTALS</td>
                <td style={{ padding: '7px 8px', fontSize: '9pt', fontWeight: 'bold', color: 'white', textAlign: 'right' }}>{formatOMR(totalSales)}</td>
                <td style={{ padding: '7px 8px', fontSize: '9pt', fontWeight: 'bold', color: 'white', textAlign: 'right' }}>{formatOMR(totalProject)}</td>
                <td style={{ padding: '7px 8px', fontSize: '10pt', fontWeight: 'bold', color: 'white', textAlign: 'right' }}>{formatOMR(totalSales + totalProject)}</td>
                <td style={{ padding: '7px 8px', fontSize: '9pt', fontWeight: 'bold', color: 'white', textAlign: 'right' }}>{formatOMR(totalPaid)}</td>
                <td style={{ padding: '7px 8px', fontSize: '9pt', fontWeight: 'bold', color: 'white', textAlign: 'right' }}>{formatOMR(totalPending)}</td>
              </tr>
            </tfoot>
          </table>

          {/* Signature Section */}
          <div style={{ marginTop: '40px', borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  {['Prepared By', 'HR / Finance Reviewed By', 'Approved By']?.map((sig, i) => (
                    <td key={i} style={{ width: '33%', paddingRight: '20px', verticalAlign: 'bottom' }}>
                      <div style={{ borderTop: '1px solid #374151', paddingTop: '6px', marginTop: '40px' }}>
                        <div style={{ fontSize: '9pt', fontWeight: 'bold', color: '#1a3a5c' }}>{sig}</div>
                        <div style={{ fontSize: '8pt', color: '#9ca3af', marginTop: '2px' }}>Name & Date</div>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div style={{ marginTop: '16px', borderTop: '1px solid #e5e7eb', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '8pt', color: '#9ca3af' }}>
            <span>{COMPANY_NAME} — Confidential</span>
            <span>Generated: {generatedDate} by {generatedBy}</span>
            <span>Page 1</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default EmployeeIncentiveReport;
